import { useEffect, useState } from 'react';
import { Configuration, SkusApi } from '@/api';
import { ProductSearchFacetCountMode, SkuFilters, SkuSort } from '@/api/SkusApi';
import { useSettings } from '@/contexts/SettingsContext';
import { ProductSearchResult, Sku, SkuAssociation } from '@/models/Sku';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { EmptyResult } from '@/modules/store/models/Pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState: ProductSearchResult = {
  ...EmptyResult<Sku>(),
  facets: [],
};

const useSearchSkusQuery = (
  page: number,
  searchQuery?: string,
  filters?: Partial<SkuFilters>,
  sort?: SkuSort[],
  include: SkuAssociation[] = [SkuAssociation.PRODUCT],
  enabled = true,
  storeId?: string | null,
  facetCountMode: ProductSearchFacetCountMode = 'window',
) => {
  const queryClient = useQueryClient();

  const [result, setResult] = useState<ProductSearchResult>(initialState);

  const { currencyCode, languageCode } = useSettings();
  const skusApi = new SkusApi(new Configuration(useAuth), { currencyCode, languageCode });

  const itemsPerPage =
    Array.isArray(filters?.skuId) && filters?.skuId.length > 0 ? filters?.skuId.length : 10;
  const searchFilters: Partial<SkuFilters> = {
    ...filters,
    storeId: storeId ?? filters?.storeId,
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      'sku-search',
      page,
      searchQuery,
      searchFilters,
      sort,
      include,
      facetCountMode,
      currencyCode,
      languageCode,
    ],
    queryFn: () => {
      const offset = page * itemsPerPage - itemsPerPage;
      return skusApi.searchSkus(
        searchQuery,
        searchFilters,
        offset,
        itemsPerPage,
        sort,
        include,
        facetCountMode,
      );
    },
    enabled,
  });

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['sku-search', page, searchQuery, searchFilters],
    });
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setResult(data || initialState);
  }, [data, isLoading, isFetching]);

  return {
    result,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useSearchSkusQuery;
